import { Popconfirm } from 'antd';
import React from 'react';

import type Tearsheet from 'stores/Tearsheet';
import Tearsheets from 'stores/Tearsheets';

import DrawerButton from '../DrawerButton';

export interface RemoveTearsheetButtonProps {
  tearsheet: Tearsheet;
}

function RemoveTearsheetButton({ tearsheet }: RemoveTearsheetButtonProps) {
  // Tearsheet.parent is a union - see the note there - so this narrows
  // rather than assuming, unlike the equivalent buttons for assets,
  // bundles, templates and video bundles, whose documents have only one
  // kind of owner.
  //
  // The narrowing is not defending against a reachable case. This component
  // is only rendered by TearsheetSummaryRow, only rendered by
  // TearsheetsDrawer, which maps `store.creative.tearsheets.allAvailable` -
  // so every tearsheet arriving here is a collection child and `canRemove`
  // is always true. The RootStore-parented tearsheet belongs to the
  // standalone /tearsheet view, which has no remove button. Nor could it:
  // that route calls loadTearsheet and nothing else, so there is no
  // creative loaded and therefore no tearsheets collection to remove from.
  //
  // So this is not a get-it-compiling guard. It is what the types honestly
  // support: the prop is a Tearsheet, a Tearsheet's parent is genuinely one
  // of two things, and only one of them can remove. The alternative,
  // `store.creative?.tearsheets`, would compile just as well while removing
  // from *a* tearsheets collection rather than the one this tearsheet
  // belongs to - the coincidence-is-not-correctness problem fixed across
  // the stores in deeebbfb. Worth noting that spelling was already broken
  // for the RootStore-parented case, where store.creative is undefined and
  // it threw.
  const collection = tearsheet.parent;
  const canRemove = collection instanceof Tearsheets;

  const removeTearsheet = async () => {
    if (canRemove) {
      await collection.remove(tearsheet.id);
    }
  };

  return (
    <Popconfirm
      cancelText="No"
      okText="Yes"
      onConfirm={removeTearsheet}
      placement="bottomRight"
      title="Are you sure you want to remove this tearsheet?"
    >
      <DrawerButton disabled={!canRemove}>Remove</DrawerButton>
    </Popconfirm>
  );
}

export default RemoveTearsheetButton;
